import type { News } from '~api';
import type { RegionPages } from './region-pages';
import type { NewsItem } from './types';

// ---------------------------------------------------------------------------------------------
// Live news helpers (UI round 2, Lane F). Home's "Recent news" reads `news.list` (the legacy
// backfill, 8,739 stories) — the hooks are in `home.ts`; these are the pure parts.
// ---------------------------------------------------------------------------------------------

/** @desc How much of a story's body stands in for a missing excerpt — about three card lines. */
export const EXCERPT_LENGTH = 220;

/**
 * @desc The card's excerpt: the story's own when it has one, else the opening of its body cut at a
 * word boundary with an ellipsis. Legacy stories carry a body and no excerpt, so without this the
 * three-line excerpt Figma draws (H5) would be empty on every card. Empty when there is neither.
 */
export const excerptOf = (
  item: Pick<News.Item, 'excerpt' | 'body'>,
  maxLength = EXCERPT_LENGTH,
): string => {
  const source = item.excerpt?.trim() || item.body?.trim() || '';
  const [firstParagraph = ''] = source.split(/\n\s*\n/);
  const text = firstParagraph.replace(/\s+/g, ' ').trim();
  if (text.length <= maxLength) return text;
  const cut = text.slice(0, maxLength);
  const atWord = cut.lastIndexOf(' ');
  return `${(atWord > maxLength / 2 ? cut.slice(0, atWord) : cut).replace(/[,;:.]$/, '')}…`;
};

/**
 * @desc Where a news card goes (H5): the original article when the story links out; else the first
 * operator group it is tagged with; else the first of its markets that has a page (tags run most
 * specific first, so a story tagged `republic-of-georgia`, `europe` opens Europe when the portal
 * has no page for Georgia); else nowhere — the card is then an article, not a link, rather than a
 * link that goes to "Region not found". `regions` is {@link useRegionPages}' answer; until it has
 * loaded (`undefined`) no market is linked, so a link never appears before it is known good.
 */
export type NewsDestination =
  | { kind: 'external'; href: string }
  | { kind: 'operator' | 'region'; slug: string }
  | { kind: 'none' };

export const newsDestination = (
  item: Pick<News.Item, 'url' | 'groupSlugs' | 'marketSlugs'>,
  regions: Pick<RegionPages, 'get'> | undefined,
): NewsDestination => {
  if (item.url) return { kind: 'external', href: item.url };
  const [group] = item.groupSlugs;
  if (group) return { kind: 'operator', slug: group };
  const market = regions ? item.marketSlugs.find((slug) => regions.get(slug)) : undefined;
  if (market) return { kind: 'region', slug: market };
  return { kind: 'none' };
};

/**
 * ILLUSTRATIVE FIXTURES — the platform-wide news feed, standing in for the CMS news source until
 * it is wired. Headlines are written to read like trade-press coverage; they are not real
 * articles, and the page renders them with the Illustrative badge. Newest first.
 */
export const NEWS: readonly NewsItem[] = [
  {
    id: 'news-global-trillion',
    headline: 'Global Gambling Market to Exceed $1 Trillion GGR by 2030',
    source: 'H2 Gambling Capital',
    date: '2026-10-12',
    summary:
      'H2’s latest forecast puts global gross gaming yield, land and online combined, above $1 trillion by the end of the decade, with online growing three times as fast.',
  },
  {
    id: 'news-brazil-blocking',
    headline: 'Brazil steps up payment blocking of unlicensed betting sites',
    source: 'iGaming Business',
    date: '2026-10-09',
    summary:
      'The SPA has added more than 2,000 domains to its blocklist as licensed operators report rising deposits.',
    regionSlugs: ['brazil'],
  },
  {
    id: 'news-entain-q3',
    headline: 'Entain Q3 trading: UK online returns to growth',
    source: 'Reuters',
    date: '2026-10-07',
    summary:
      'The group says UK online net gaming revenue grew for the third consecutive quarter, while BetMGM raised its full-year guidance.',
    groupSlugs: ['entain'],
    regionSlugs: ['uk'],
  },
  {
    id: 'news-spain-share',
    headline: 'Spain’s online market grows 11% as casino overtakes sports betting',
    source: 'EGR Global',
    date: '2026-10-03',
    summary:
      'DGOJ figures show online casino is now the largest vertical; bet365 and Codere remain the top two operators.',
    regionSlugs: ['spain'],
  },
  {
    id: 'news-alberta-launch',
    headline: 'Alberta sets launch window for competitive online market',
    source: 'SBC News',
    date: '2026-09-30',
    summary:
      'The AGLC expects the first operator licences to go live early next year, following the Ontario model.',
    regionSlugs: ['alberta'],
  },
  {
    id: 'news-flutter-italy',
    headline: 'Flutter completes Snaitech integration, targets Italian online leadership',
    source: 'Financial Times',
    date: '2026-09-26',
    groupSlugs: ['flutter-entertainment'],
    regionSlugs: ['italy'],
  },
  {
    id: 'news-netherlands-tax',
    headline: 'Dutch operators warn of black-market shift as gaming tax rises again',
    source: 'Gambling Insider',
    date: '2026-09-19',
    regionSlugs: ['netherlands'],
  },
  {
    id: 'news-draftkings-states',
    headline: 'DraftKings eyes new-state launches as US sports betting matures',
    source: 'Reuters',
    date: '2026-09-12',
    groupSlugs: ['draftkings'],
  },
];
